
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ICONS } from '../constants';
import clsx from 'clsx';

interface OnboardingStep {
  title: string;
  description: string;
  icon: any;
  position: 'center' | 'top-right' | 'top-left' | 'bottom-center' | 'top-center';
  target?: string; 
}

const STEPS: OnboardingStep[] = [
  {
    title: "Welcome to ReShooter",
    description: "Your autonomous AI photography studio. Let's show you how to turn any photo into a masterpiece in seconds.",
    icon: ICONS.Aperture,
    position: 'center',
  },
  {
    title: "The Upload Hub",
    description: "Start here. Upload any photo—even a blurry selfie—and our AI will reconstruct the lighting and geometry.",
    icon: ICONS.Plus,
    position: 'center',
    target: '.upload-area'
  },
  {
    title: "The Reshoot System",
    description: "Each high-end transformation costs 1 RESHOOT. You start with 5 for free! Upgrade anytime for more capacity.",
    icon: ICONS.Zap,
    position: 'center',
    target: '.reshoot-counter'
  },
  {
    title: "Studio Controls",
    description: "Want more control? Toggle Customization to change backgrounds, props, camera gear, and poses manually.",
    icon: ICONS.Sliders,
    position: 'center',
    target: '.customize-btn'
  },
  {
    title: "Your Gallery",
    description: "Everything you create is saved to your private cloud gallery. Access your history here anytime.",
    icon: ICONS.Layers,
    position: 'center',
    target: '.gallery-btn'
  },
  {
    title: "Ready to Create?",
    description: "You're all set. Upload your first image and see the magic happen.",
    icon: ICONS.Sparkles,
    position: 'center'
  }
];

export const Onboarding: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const step = STEPS[currentStep];

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center pointer-events-auto overflow-hidden">
      {/* Dimmed Background */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] pointer-events-none"
      />

      {/* PERSISTENT BOX: This container stays in the same spot throughout the onboarding */}
      <div className="relative z-[310] w-full max-w-sm m-4 bg-zinc-950 border border-white/20 rounded-2xl p-8 shadow-[0_0_80px_rgba(0,0,0,0.6)] flex flex-col items-center text-center">
        
        <AnimatePresence mode="wait">
          {/* CONTENT-ONLY ANIMATION: Only the icon, title, and description animate out and in */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="flex flex-col items-center w-full"
          >
            {/* Animated Icon Container */}
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-6 ring-1 ring-white/20 relative">
              <div className="absolute inset-0 bg-white/5 blur-xl rounded-full" />
              <step.icon className="w-8 h-8 text-white relative z-10" />
            </div>

            <h2 className="text-xl font-bold text-white mb-3 tracking-tight uppercase">
              {step.title}
            </h2>
            
            <p className="text-sm text-zinc-400 leading-relaxed mb-8 font-medium">
              {step.description}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* PERSISTENT CONTROLS: Buttons stay in place */}
        <div className="w-full flex flex-col gap-3">
          <button
            onClick={handleNext}
            className="w-full py-3 bg-white text-black font-black uppercase text-[10px] tracking-[0.2em] rounded-lg hover:bg-zinc-200 transition-all active:scale-95 shadow-xl"
          >
            {currentStep === STEPS.length - 1 ? "Start Creating" : "Next Step"}
          </button>
          
          {currentStep < STEPS.length - 1 && (
            <button
              onClick={onComplete}
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white transition-colors py-2 font-bold"
            >
              Skip Tutorial
            </button>
          )}
        </div>

        {/* Pagination Indicators */}
        <div className="flex gap-1.5 mt-8">
          {STEPS.map((_, i) => (
            <div 
              key={i} 
              className={clsx(
                "h-1 rounded-full transition-all duration-300",
                i === currentStep ? "w-6 bg-white" : "w-1.5 bg-zinc-800"
              )} 
            />
          ))}
        </div>
      </div>

      {/* Spotlight for targeted elements remains if target is present */}
      <AnimatePresence>
        {step.target && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[295] pointer-events-none"
            >
                <div className="absolute inset-0 bg-white/[0.04] animate-pulse" />
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
